/**
 * Country → Language mapping for auto-translation of outreach emails.
 * User writes email in English → system translates to recipient's local language.
 */

interface LanguageInfo {
    code: string;    // ISO 639-1
    name: string;    // English name
    nativeName: string; // Native script name
    flag: string;
}

const COUNTRY_LANGUAGE: Record<string, LanguageInfo> = {
    // Americas - English
    US: { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
    CA: { code: 'en', name: 'English', nativeName: 'English', flag: '🇨🇦' },
    AU: { code: 'en', name: 'English', nativeName: 'English', flag: '🇦🇺' },
    NZ: { code: 'en', name: 'English', nativeName: 'English', flag: '🇳🇿' },
    GB: { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
    IE: { code: 'en', name: 'English', nativeName: 'English', flag: '🇮🇪' },
    SG: { code: 'en', name: 'English', nativeName: 'English', flag: '🇸🇬' },
    IN: { code: 'en', name: 'English', nativeName: 'English', flag: '🇮🇳' },
    PH: { code: 'en', name: 'English', nativeName: 'English', flag: '🇵🇭' },
    HK: { code: 'en', name: 'English', nativeName: 'English', flag: '🇭🇰' },
    IL: { code: 'en', name: 'English', nativeName: 'English', flag: '🇮🇱' },

    // Americas - Spanish
    MX: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇲🇽' },
    AR: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇦🇷' },
    UY: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇺🇾' },
    CO: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇨🇴' },
    CL: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇨🇱' },
    PE: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇵🇪' },
    EC: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇨' },
    VE: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇻🇪' },
    CR: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇨🇷' },
    PA: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇵🇦' },
    DO: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇩🇴' },
    GT: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇬🇹' },
    ES: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },

    // Americas - Portuguese
    BR: { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
    PT: { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },

    // Europe
    FR: { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
    BE: { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇧🇪' },
    DE: { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
    AT: { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇦🇹' },
    CH: { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇨🇭' },
    IT: { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
    NL: { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
    SE: { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
    NO: { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴' },
    DK: { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰' },
    FI: { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮' },
    PL: { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
    CZ: { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
    RO: { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },

    // Asia-Pacific
    CN: { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
    TW: { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇹🇼' },
    JP: { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
    KR: { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
    TH: { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
    VN: { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
    MY: { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾' },
    ID: { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },

    // Middle East
    AE: { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇦🇪' },
    SA: { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
};

const DEFAULT_LANG: LanguageInfo = { code: 'en', name: 'English', nativeName: 'English', flag: '🌐' };

/**
 * Get the language info for a country code.
 */
export function getCountryLanguage(countryCode: string): LanguageInfo {
    return COUNTRY_LANGUAGE[countryCode] || DEFAULT_LANG;
}

/**
 * Check if a country sends emails in English (no translation needed).
 */
export function isEnglishCountry(countryCode: string): boolean {
    return (COUNTRY_LANGUAGE[countryCode]?.code || 'en') === 'en';
}

/**
 * Get unique languages from a list of country codes.
 */
export function getLanguagesForCountries(countryCodes: string[]): LanguageInfo[] {
    const seen = new Set<string>();
    const result: LanguageInfo[] = [];
    for (const code of countryCodes) {
        const lang = getCountryLanguage(code);
        if (!seen.has(lang.code)) {
            seen.add(lang.code);
            result.push(lang);
        }
    }
    return result;
}
